import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const anchors: {
  name: string;
  jurisdiction: string;
  market: string;
  status: string;
  bullets: ReactNode[];
}[] = [
  {
    name: "Raymond Legal",
    jurisdiction: "United Kingdom",
    market: "UK legal market",
    status: "ANCHOR",
    bullets: [
      <>Established bespoke law firm, <B>£2.5M/year</B> practice.</>,
      <>MSA signed. COSX deploying full AI back-office across existing caseload.</>,
      <>Opening mid-tail legal services: conveyancing, wills, family law, IP.</>,
      <>Joint venture: <B>100% AI-delivered immigration service</B>. Equity model in pilot.</>,
    ],
  },
  {
    name: "Ratner",
    jurisdiction: "Singapore",
    market: "Asia legal market",
    status: "ANCHOR",
    bullets: [
      <>SME law firm anchoring SE Asia.</>,
      <>MSA signed. AI agents deploying.</>,
      <>New <B>back-office entity</B> being established to serve regulated firms across SE Asia.</>,
    ],
  },
];

const supporting: {
  name: string;
  jurisdiction: string;
  market: string;
  status: string;
  bullets: ReactNode[];
}[] = [
  {
    name: "ProActive Legal",
    jurisdiction: "Australia / New Zealand",
    market: "AU/NZ legal market",
    status: "LIVE",
    bullets: [
      <>Immigration agents operational across AU/NZ caseload.</>,
      <>Reference deployment for our paralegal agent suite.</>,
    ],
  },
  {
    name: "RidgeField Capital",
    jurisdiction: "Singapore",
    market: "Asia fund compliance",
    status: "MSA IN PROGRESS",
    bullets: [
      <>First back-office client for <B>fund ops & compliance agents</B>.</>,
      <>Compliance review module in early deployment.</>,
    ],
  },
];

export function V3PartnersSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[28px] font-light tracking-tight leading-tight mb-1 font-[family-name:var(--font-heading)]">
        Two anchor partnerships. Two supporting deployments.
      </h2>
      <p className="text-[13px] text-[var(--color-fg-secondary)] font-light mb-4">
        Three jurisdictions. <strong className="font-medium text-[var(--color-fg)]">All signed or live.</strong>
      </p>

      {/* Anchor partners — 2 large cards with accent top-bar + ANCHOR blue pill */}
      <div className="grid grid-cols-2 gap-4 mb-3">
        {anchors.map((a) => (
          <div key={a.name} className="border border-[var(--color-border)] rounded-sm overflow-hidden flex flex-col">
            <div className="h-[3px] bg-[var(--color-accent)]" />
            <div className="px-4 py-3 flex flex-col">
              <div className="flex items-baseline gap-2 mb-0.5">
                <h3 className="text-[14px] font-medium font-[family-name:var(--font-heading)]">{a.name}</h3>
                <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-[var(--color-accent)]/15 text-[var(--color-accent)] ml-auto">
                  {a.status}
                </span>
              </div>
              <div className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider mb-2">
                {a.jurisdiction} · {a.market}
              </div>
              <ul className="space-y-1.5">
                {a.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--color-fg-secondary)] leading-relaxed">
                    <span className="w-1 h-1 rounded-full bg-[var(--color-fg-tertiary)] mt-[6px] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Supporting deployments — 2 compact cards, neutral status pill, short bullets */}
      <div className="mb-2">
        <div className="text-[11px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg-tertiary)] uppercase tracking-[0.1em] mb-2">
          Also live
        </div>
        <div className="grid grid-cols-2 gap-3">
          {supporting.map((s) => (
            <div key={s.name} className="border border-[var(--color-border)] rounded-sm px-3 py-2.5">
              <div className="flex items-baseline gap-2 mb-0.5">
                <h4 className="text-[12px] font-medium">{s.name}</h4>
                <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-[var(--color-fg-secondary)]/10 text-[var(--color-fg-secondary)] ml-auto">
                  {s.status}
                </span>
              </div>
              <div className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider mb-1.5">
                {s.jurisdiction} · {s.market}
              </div>
              <ul className="space-y-1">
                {s.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--color-fg-secondary)] leading-snug">
                    <span className="w-1 h-1 rounded-full bg-[var(--color-fg-tertiary)] mt-[6px] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 text-[11px] text-[var(--color-fg-secondary)] font-light leading-relaxed">
        Plus <strong className="font-medium text-[var(--color-fg)]">20-50 firms</strong> in active discussion through low-cost AI services.
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] font-medium text-[var(--color-fg)]">
          All signed or live. Not pipeline. Action.
        </p>
      </div>
    </div>
  );
}
